import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import Spinner from 'components/Icons/Spinner';
import { cn } from 'lib/utils';
import { ButtonGroup } from './button-group';

const colorClasses = {
  blue: 'text-white bg-blue-700 border border-transparent hover:bg-blue-800 focus:ring-4 focus:ring-blue-300 disabled:hover:bg-blue-700',
  royalblue:
    'text-white bg-royal-blue-700 border border-transparent hover:bg-royal-blue-800 focus:ring-4 focus:ring-royal-blue-300 disabled:hover:bg-royal-blue-700',
  alternative:
    'text-gray-900 bg-white border border-gray-200 hover:bg-gray-100 hover:text-blue-700 disabled:hover:bg-white focus:ring-blue-700 focus:text-blue-700 focus:ring-2',
  dark: 'text-white bg-gray-800 border border-transparent hover:bg-gray-900 focus:ring-4 focus:ring-gray-300 disabled:hover:bg-gray-800',
  light:
    'text-gray-900 bg-white border border-gray-300 hover:bg-gray-100 focus:ring-4 focus:ring-blue-300 disabled:hover:bg-white',
  green:
    'text-white bg-green-700 border border-transparent hover:bg-green-800 focus:ring-4 focus:ring-green-300 disabled:hover:bg-green-700',
  lime: 'bg-lime-300 border border-transparent hover:bg-lime-400 focus:ring-4 focus:ring-lime-300 disabled:hover:bg-lime-700',
  red: 'text-white bg-red-700 border border-transparent hover:bg-red-800 focus:ring-4 focus:ring-red-300 disabled:hover:bg-red-800',
  yellow:
    'text-white bg-yellow-400 border border-transparent hover:bg-yellow-500 focus:ring-4 focus:ring-yellow-300 disabled:hover:bg-yellow-400',
  purple:
    'text-white bg-purple-700 border border-transparent hover:bg-purple-800 focus:ring-4 focus:ring-purple-300 disabled:hover:bg-purple-700',
  transparent:
    'text-gray border-transparent focus:ring-4 focus:ring-transparent hover:text-gray-600',
} as const;

const gradientMonochromeClasses = {
  blue: 'text-white bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700 hover:bg-gradient-to-br focus:ring-4 focus:ring-blue-300',
  green:
    'text-white bg-gradient-to-r from-green-400 via-green-500 to-green-600 hover:bg-gradient-to-br focus:ring-4 focus:ring-green-300',
  cyan: 'text-white bg-gradient-to-r from-cyan-400 via-cyan-500 to-cyan-600 hover:bg-gradient-to-br focus:ring-4 focus:ring-cyan-300',
  teal: 'text-white bg-gradient-to-r from-teal-400 via-teal-500 to-teal-600 hover:bg-gradient-to-br focus:ring-4 focus:ring-teal-300',
  lime: 'text-gray-900 bg-gradient-to-r from-lime-200 via-lime-400 to-lime-500 hover:bg-gradient-to-br focus:ring-4 focus:ring-lime-300',
  red: 'text-white bg-gradient-to-r from-red-400 via-red-500 to-red-600 hover:bg-gradient-to-br focus:ring-4 focus:ring-red-300',
  pink: 'text-white bg-gradient-to-r from-pink-400 via-pink-500 to-pink-600 hover:bg-gradient-to-br focus:ring-4 focus:ring-pink-300',
  purple:
    'text-white bg-gradient-to-r from-purple-500 via-purple-600 to-purple-700 hover:bg-gradient-to-br focus:ring-4 focus:ring-purple-300',
} as const;

const gradientDuoToneClasses = {
  purpleToBlue:
    'text-white bg-gradient-to-br from-purple-600 to-blue-500 hover:bg-gradient-to-bl focus:ring-4 focus:ring-blue-300',
  cyanToBlue:
    'text-white bg-gradient-to-r from-cyan-500 to-blue-500 hover:bg-gradient-to-bl focus:ring-4 focus:ring-cyan-300',
  greenToBlue:
    'text-white bg-gradient-to-br from-green-400 to-blue-600 hover:bg-gradient-to-bl focus:ring-4 focus:ring-green-200',
  purpleToPink:
    'text-white bg-gradient-to-r from-purple-500 to-pink-500 hover:bg-gradient-to-l focus:ring-4 focus:ring-purple-200',
  pinkToOrange:
    'text-white bg-gradient-to-br from-pink-500 to-orange-400 hover:bg-gradient-to-bl focus:ring-4 focus:ring-pink-200',
  tealToLime:
    'text-gray-900 bg-gradient-to-r from-teal-200 to-lime-200 hover:bg-gradient-to-l hover:from-teal-200 hover:to-lime-200 hover:!text-gray-900 focus:ring-4 focus:ring-lime-200',
  redToYellow:
    'text-gray-900 bg-gradient-to-r from-red-200 via-red-300 to-yellow-200 hover:bg-gradient-to-bl focus:ring-4 focus:ring-red-100',
} as const;

const buttonVariants = cva(
  'group inline-flex h-min w-fit items-center justify-center p-0.5 text-center font-medium focus:z-10 transition-colors disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      pill: {
        true: 'rounded-full',
        false: 'rounded-lg',
      },
      position: {
        none: '',
        start: 'rounded-r-none',
        middle: '!rounded-none border-l-0 pl-0',
        end: 'rounded-l-none border-l-0 pl-0',
      },
    },
    defaultVariants: {
      pill: false,
      position: 'none',
    },
  }
);

const buttonInnerVariants = cva('flex items-center', {
  variants: {
    size: {
      xs: 'text-xs px-1 py-1',
      sm: 'text-sm px-3 py-1.5',
      md: 'text-sm px-4 py-2',
      lg: 'text-base px-5 py-2.5',
      xl: 'text-base px-6 py-3',
    },
    pill: {
      true: 'rounded-full',
      false: 'rounded-md',
    },
    outline: {
      true: 'bg-white text-gray-900 transition-all duration-75 ease-in group-hover:bg-opacity-0 group-hover:text-inherit',
      false: '',
    },
    position: {
      none: '',
      start: 'rounded-r-none',
      middle: '!rounded-none',
      end: 'rounded-l-none',
    },
  },
  defaultVariants: {
    size: 'md',
    pill: false,
    outline: false,
    position: 'none',
  },
});

const iconPaddingVariants = {
  xs: '!px-0.5',
  sm: '!px-1.5',
  md: '!px-2',
  lg: '!p-2.5',
  xl: '!p-3',
} as const;

const iconDimensionVariants = {
  xs: 'w-2 h-2',
  sm: 'w-4 h-4',
  md: 'w-5 h-5',
  lg: 'w-6 h-6',
  xl: 'w-7 h-7',
} as const;

type ButtonVariants = VariantProps<typeof buttonVariants>;
type ButtonInnerVariants = VariantProps<typeof buttonInnerVariants>;

export type ButtonColor = keyof typeof colorClasses;
export type ButtonSize = NonNullable<ButtonInnerVariants['size']>;
export type GradientMonochrome = keyof typeof gradientMonochromeClasses;
export type GradientDuoTone = keyof typeof gradientDuoToneClasses;
export type PositionInGroup = Exclude<
  NonNullable<ButtonVariants['position']>,
  'none'
>;

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  asChild?: boolean;
  pill?: boolean;
  outline?: boolean;
  label?: React.ReactNode;
  color?: ButtonColor;
  size?: ButtonSize;
  icon?: React.ComponentType<{ className?: string }>;
  gradientMonochrome?: GradientMonochrome;
  gradientDuoTone?: GradientDuoTone;
  positionInGroup?: PositionInGroup;
  isLoading?: boolean;
};

const ButtonComponent = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      asChild = false,
      pill = false,
      outline = false,
      label,
      color = 'blue',
      size = 'md',
      icon: Icon,
      gradientMonochrome,
      gradientDuoTone,
      positionInGroup,
      isLoading = false,
      className,
      children,
      disabled,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';
    const resolvedPosition = positionInGroup ?? 'none';
    const {
      type,
      ['data-testid']: dataTestId,
      ...restProps
    } = props as ButtonProps & { ['data-testid']?: string };

    return (
      <Comp
        ref={ref}
        className={cn(
          buttonVariants({
            pill,
            position: resolvedPosition,
          }),
          positionInGroup && 'focus:!ring-2',
          outline && color === 'alternative' && 'border border-gray-900',
          !gradientMonochrome && !gradientDuoTone && colorClasses[color],
          gradientMonochrome && gradientMonochromeClasses[gradientMonochrome],
          gradientDuoTone && gradientDuoToneClasses[gradientDuoTone],
          className
        )}
        disabled={disabled}
        data-testid={dataTestId ?? 'button-element'}
        {...(!asChild && { type: type ?? 'button' })}
        {...restProps}
      >
        <span
          className={cn(
            buttonInnerVariants({
              size,
              pill,
              outline,
              position: resolvedPosition,
            }),
            Icon && iconPaddingVariants[size]
          )}
        >
          {isLoading ? (
            <Spinner
              className={cn(iconDimensionVariants[size], 'animate-spin')}
            />
          ) : (
            Icon && (
              <Icon
                className={cn(
                  iconDimensionVariants[size],
                  (label || children) && 'mr-2'
                )}
              />
            )
          )}
          {children}
          {label && (
            <span className="ml-4 inline-flex h-4 w-4 items-center justify-center rounded-full bg-blue-200 text-xs font-semibold text-blue-800">
              {label}
            </span>
          )}
        </span>
      </Comp>
    );
  }
);

ButtonComponent.displayName = 'Button';

const Button = Object.assign(ButtonComponent, {
  Group: ButtonGroup,
});

export { Button, ButtonGroup, buttonVariants };
